import { useId, useRef, useState, type KeyboardEvent } from "react";
import { Check, ChevronDown } from "lucide-react";
import type { OrderTypeRow } from "../types";

interface OrderTypeSelectProps {
  options: OrderTypeRow[];
  value: string;
  onChange: (id: string) => void;
}

export function OrderTypeSelect({ options, value, onChange }: OrderTypeSelectProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const listboxId = useId();
  const selectedIndex = options.findIndex((option) => option.id === value);
  const selected = options[selectedIndex];

  function focusOption(index: number) {
    requestAnimationFrame(() => optionRefs.current[index]?.focus());
  }

  function openMenu(index = Math.max(0, selectedIndex)) {
    if (options.length === 0) return;
    setOpen(true);
    focusOption(index);
  }

  function selectOption(id: string) {
    onChange(id);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function handleTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openMenu();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      openMenu(selectedIndex >= 0 ? selectedIndex : options.length - 1);
    }
  }

  function handleOptionKeyDown(event: KeyboardEvent<HTMLDivElement>, index: number) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      focusOption((index + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length);
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      focusOption(event.key === "Home" ? 0 : options.length - 1);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const option = options[index];
      if (option) selectOption(option.id);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
  }

  return (
    <div
      className="relative w-full"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        id="orderType"
        type="button"
        className={`w-full min-h-[46px] px-3.5 py-2.5 flex items-center justify-between text-left text-sm font-medium rounded-xl border bg-white transition-all outline-none ${
          open
            ? "border-[#012699] ring-3 ring-[#012699]/15 shadow-xs"
            : "border-slate-300 hover:border-slate-400"
        } ${selected ? "text-slate-900" : "text-slate-400"} disabled:opacity-50 disabled:bg-slate-50 disabled:cursor-not-allowed`}
        aria-labelledby="orderTypeLabel orderType"
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={handleTriggerKeyDown}
        disabled={options.length === 0}
      >
        <span className="truncate">{selected?.label ?? "Select order type"}</span>
        <ChevronDown
          size={18}
          className={`shrink-0 text-slate-400 transition-transform duration-200 ${
            open ? "rotate-180 text-[#012699]" : ""
          }`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div
          id={listboxId}
          className="absolute z-50 left-0 right-0 mt-1.5 max-h-60 overflow-y-auto bg-white rounded-xl border border-slate-200 shadow-xl py-1.5 focus:outline-none animate-in fade-in-50 zoom-in-95 duration-150"
          role="listbox"
          aria-labelledby="orderTypeLabel"
        >
          {options.map((option, index) => {
            const isSelected = option.id === value;
            return (
              <div
                key={option.id}
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                className={`flex items-center justify-between px-3.5 py-2.5 text-sm cursor-pointer transition-colors outline-none ${
                  isSelected
                    ? "bg-[#012699]/10 text-[#012699] font-semibold"
                    : "text-slate-700 hover:bg-slate-50 focus:bg-slate-50"
                }`}
                role="option"
                aria-selected={isSelected}
                tabIndex={-1}
                onClick={() => selectOption(option.id)}
                onKeyDown={(event) => handleOptionKeyDown(event, index)}
              >
                <span className="truncate">{option.label}</span>
                {isSelected && <Check size={16} className="text-[#012699] shrink-0 ml-2" aria-hidden="true" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
